import { Box, Typography } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import PropTypes from "prop-types";
import useSWR from "swr";
import { fetcher } from "../../hooks";
import { refreshShopSetupStatusEventName, useEvent } from "../../util/events";

const StyledCard = styled("div")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.warning.light,
  border: `2px solid ${theme.palette.warning.main}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.boxShadow.faint,
  color: theme.palette.getContrastText(theme.palette.warning.light),
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(2),
}));

const StyledIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  fontSize: theme.typography.h1.fontSize,
}));

function useShopSetupStatus(influencerId) {
  const { data, error, mutate } = useSWR(
    `/api/influencer/isSetupCorrectly/${influencerId}`,
    fetcher,
  );

  return {
    error,
    isError: !!error,
    isLoading: !error && !data,
    message: data?.message,
    refresh: mutate,
    success: data?.success,
    errorDetails: data?.errors,
  };
}

function ErrorDetails({ errorDetails }) {
  if (!Array.isArray(errorDetails) || errorDetails.length === 0) {
    return null;
  }

  return (
    <Box mt={1}>
      {errorDetails.map((detail, index) => (
        <Typography variant="caption" key={index} component="p">
          <strong>Product:</strong> {detail.productName} -{" "}
          <strong>Split:</strong> {detail.shopName}
        </Typography>
      ))}
    </Box>
  );
}

ErrorDetails.propTypes = {
  errorDetails: PropTypes.arrayOf(
    PropTypes.shape({
      productName: PropTypes.string,
      shopName: PropTypes.string,
    }),
  ),
};

export default function ShopNotSetup({ influencer }) {
  const { success, message, errorDetails, isLoading, isError, refresh } =
    useShopSetupStatus(influencer.influencerId);

  useEvent(refreshShopSetupStatusEventName, refresh);

  if (success || isLoading || isError || !message) {
    return null;
  }

  return (
    <StyledCard>
      <StyledIcon />
      <div>
        <Typography variant="h6">Shop Configuration Incomplete</Typography>
        <Typography variant="body1">{message}</Typography>
        <ErrorDetails errorDetails={errorDetails} />
      </div>
    </StyledCard>
  );
}

ShopNotSetup.propTypes = {
  influencer: PropTypes.shape({
    influencerId: PropTypes.string.isRequired,
  }),
};
